import { useState } from "react";
import axios from "axios";
import { Plus, Trash2, X, Copy, Check, Link2, Loader2, Mail, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  ["SK", "Slovensko"], ["CZ", "Česko"], ["AT", "Rakúsko"], ["DE", "Nemecko"],
  ["PL", "Poľsko"], ["HU", "Maďarsko"], ["FR", "Francúzsko"], ["IT", "Taliansko"],
  ["NL", "Holandsko"], ["IE", "Írsko"], ["XX", "Iná krajina (mimo EÚ)"],
];

export default function NewInvoiceForm({ token, onClose, onCreated }) {
  const [b, setB] = useState({ buyer_name: "", buyer_email: "", buyer_address: "", buyer_ico: "", buyer_country: "SK", buyer_vat_id: "", series: 3, apply_vat: true });
  const [items, setItems] = useState([{ description: "", amount: "" }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [payUrl, setPayUrl] = useState("");
  const [bankSent, setBankSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupMsg, setLookupMsg] = useState("");
  const [sug, setSug] = useState([]);
  const [sugOpen, setSugOpen] = useState(false);

  const searchOurs = async (name) => {
    if ((name || "").trim().length < 2) { setSug([]); setSugOpen(false); return; }
    try {
      const { data } = await axios.get(`${API}/admin/applicants/search?q=${encodeURIComponent(name)}`, { headers: { Authorization: `Bearer ${token}` } });
      setSug(data || []); setSugOpen((data || []).length > 0);
    } catch (e) { setSug([]); }
  };
  const pickSug = (s) => {
    setB((p) => ({ ...p, buyer_name: s.name, buyer_email: s.email || p.buyer_email, buyer_ico: s.ico || p.buyer_ico, buyer_vat_id: s.vat_id || p.buyer_vat_id, buyer_address: s.address || p.buyer_address, buyer_country: s.country || p.buyer_country }));
    setSug([]); setSugOpen(false);
  };

  const lookupCompany = async () => {
    const ico = (b.buyer_ico || "").replace(/\D/g, "");
    if (ico.length !== 8) { setLookupMsg("Zadajte 8-miestne IČO."); return; }
    setLookupBusy(true); setLookupMsg("");
    try {
      const { data } = await axios.get(`${API}/company/lookup?ico=${ico}`);
      if (!data.found) { setLookupMsg("Firma s týmto IČO sa nenašla."); return; }
      const addr = [data.street, [data.zip, data.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
      setB((p) => ({ ...p, buyer_name: data.name || p.buyer_name, buyer_ico: data.ico || p.buyer_ico, buyer_address: addr || p.buyer_address }));
      setLookupMsg(`✓ Doplnené: ${data.name}${data.dic ? " · DIČ " + data.dic : ""}`);
    } catch (e) {
      setLookupMsg(e.response?.data?.detail || "Register nedostupný.");
    } finally { setLookupBusy(false); }
  };
  const [invoiceId, setInvoiceId] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const set = (k) => (e) => setB((p) => ({ ...p, [k]: e.target.value }));
  const setItem = (i, k) => (e) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [k]: e.target.value } : it));
  const addItem = () => setItems((a) => [...a, { description: "", amount: "" }]);
  const removeItem = (i) => setItems((a) => a.filter((_, idx) => idx !== i));
  const total = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);

  const validItems = () => items.filter((it) => it.description.trim() && parseFloat(it.amount) > 0)
    .map((it) => ({ description: it.description.trim(), amount: parseFloat(it.amount) }));

  const goPreview = () => {
    setError("");
    if (!b.buyer_name.trim() || !b.buyer_email.trim()) { setError("Vyplňte meno a e-mail odberateľa."); return; }
    if (validItems().length === 0) { setError("Pridajte aspoň jednu položku so sumou."); return; }
    setPreview(true);
  };

  const create = async (method = "stripe") => {
    setError("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/invoices/create`,
        { ...b, apply_vat: b.buyer_country === "SK" ? b.apply_vat : null, items: validItems(), origin_url: window.location.origin, method },
        { headers: { Authorization: `Bearer ${token}` } });
      setPayUrl(data.pay_url || "");
      setInvoiceId(data.invoice?.id || "");
      if (method === "bank") setBankSent(true);
      onCreated && onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || "Vytvorenie zlyhalo.");
    } finally { setBusy(false); }
  };

  const sendEmail = async () => {
    if (!invoiceId) return;
    setSendingEmail(true); setError("");
    try {
      await axios.post(`${API}/admin/invoices/${invoiceId}/send-email`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setEmailSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Odoslanie e-mailu zlyhalo.");
    } finally { setSendingEmail(false); }
  };

  const copy = () => { navigator.clipboard.writeText(payUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const vatOn = b.buyer_country === "SK" && b.apply_vat;
  const regimeHint = b.buyer_country === "SK" ? (b.apply_vat ? "Tuzemsko – DPH 23 %" : "Tuzemsko – bez DPH (služba v zahraničí)")
    : b.buyer_country === "XX" ? "Mimo EÚ – bez DPH"
    : b.buyer_vat_id ? "EÚ B2B – Reverse charge" : "EÚ – režim OSS";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="new-invoice-modal">
      <div className="bg-[#0a0a0a] border border-white/15 w-full max-w-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-display text-xl">Nová faktúra</h3>
          <button onClick={onClose} data-testid="new-invoice-close" className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        {bankSent ? (
          <div className="p-6 space-y-5" data-testid="new-invoice-bank-result">
            <div className="flex items-center gap-2 text-emerald-300"><Check size={18} /> <span>Faktúra bola odoslaná e-mailom ako PDF s Vašimi údajmi a bankovým spojením (IBAN), splatnosť 15 dní.</span></div>
            <p className="text-white/50 text-sm">Po pripísaní úhrady ju označte ako <strong className="text-[#D4AF37]">uhradenú</strong> v zozname faktúr (tlačidlo „Nezaplatené" → „Zaplatené").</p>
            <button onClick={onClose} data-testid="ni-bank-close" className="bg-[#D4AF37] text-black px-5 py-2.5 text-sm font-semibold hover:bg-[#F2E3C6] transition-colors">Hotovo</button>
            {error && <p className="text-red-400 text-sm">{error}</p>}
          </div>
        ) : payUrl ? (
          <div className="p-6 space-y-5" data-testid="new-invoice-result">
            <div className="flex items-center gap-2 text-[#D4AF37]"><Check size={18} /> <span>Faktúra vytvorená. Pošlite klientovi tento platobný odkaz:</span></div>
            <div className="flex items-center gap-2 border border-white/15 bg-[#111] p-3">
              <Link2 size={16} className="text-white/40 shrink-0" />
              <input readOnly value={payUrl} className="flex-1 bg-transparent text-white/80 text-sm focus:outline-none" data-testid="pay-link-input" />
              <button onClick={copy} data-testid="copy-pay-link" className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-black px-3 py-1.5 text-sm font-semibold">
                {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Skopírované" : "Kopírovať"}
              </button>
            </div>
            <p className="text-white/40 text-xs">Po úhrade sa klientovi automaticky pošle faktúra v PDF a objaví sa v zozname faktúr.</p>
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={sendEmail} disabled={sendingEmail || emailSent || !invoiceId} data-testid="ni-send-email"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2.5 text-sm font-semibold hover:bg-[#F2E3C6] transition-colors disabled:opacity-50">
                {emailSent ? <><Check size={15} /> Odoslané klientovi</> : sendingEmail ? <><Loader2 size={15} className="animate-spin" /> Odosielam...</> : <><Mail size={15} /> Odoslať faktúru e-mailom</>}
              </button>
              <button onClick={onClose} className="bg-white/10 hover:bg-white/20 px-5 py-2.5 text-sm">Zavrieť</button>
            </div>
            {error && <p data-testid="ni-result-error" className="text-red-400 text-sm">{error}</p>}
          </div>
        ) : preview ? (
          <div className="p-6 space-y-5" data-testid="invoice-preview">
            <p className="text-xs uppercase tracking-wider text-[#D4AF37]">Náhľad pred odoslaním — skontrolujte údaje</p>
            <div className="border border-white/10 bg-[#111] p-5 space-y-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/35 mb-1">Odberateľ</p>
                <p className="text-white text-sm font-medium" data-testid="preview-buyer-name">{b.buyer_name}</p>
                <p className="text-white/60 text-sm">{b.buyer_email}</p>
                {b.buyer_address && <p className="text-white/50 text-sm">{b.buyer_address}</p>}
                {b.buyer_ico && <p className="text-white/50 text-sm">IČO / Dát. nar.: {b.buyer_ico}</p>}
                {b.buyer_vat_id && <p className="text-white/50 text-sm">IČ DPH: {b.buyer_vat_id}</p>}
                <p className="text-white/50 text-sm">{COUNTRIES.find(([c]) => c === b.buyer_country)?.[1] || b.buyer_country}</p>
              </div>
              <div className="border-t border-white/10 pt-3 space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-white/35">Položky (bez DPH)</p>
                {validItems().map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 text-sm" data-testid="preview-item">
                    <span className="text-white/80">{it.description}</span>
                    <span className="text-white/80 shrink-0">{it.amount.toFixed(2)} €</span>
                  </div>
                ))}
              </div>
              {vatOn && (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/50">Základ dane (bez DPH)</span>
                    <span className="text-white/70">{total.toFixed(2)} €</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/50">DPH 23 %</span>
                    <span className="text-white/70">{(total * 0.23).toFixed(2)} €</span>
                  </div>
                </>
              )}
              <div className="flex items-center justify-between border-t border-white/10 pt-3">
                <span className="text-white/60">Spolu na úhradu</span>
                <span className="font-display text-2xl text-[#D4AF37]" data-testid="preview-total">{(vatOn ? total * 1.23 : total).toFixed(2)} €</span>
              </div>
              <p className="text-white/40 text-xs">Daňový režim: <span className="text-[#D4AF37]">{regimeHint}</span></p>
            </div>
            <p className="text-white/40 text-xs">Vyberte spôsob: <span className="text-[#D4AF37]">platobný odkaz (karta, Stripe)</span> alebo <span className="text-[#D4AF37]">PDF s bankovým prevodom</span> (splatnosť 15 dní, úhradu označíte ručne).</p>
            {error && <p data-testid="preview-error" className="text-red-400 text-sm">{error}</p>}
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={() => create("stripe")} disabled={busy} data-testid="preview-confirm"
                className="flex-1 min-w-[180px] bg-[#D4AF37] text-black font-semibold py-3 hover:bg-[#F2E3C6] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {busy ? <><Loader2 size={16} className="animate-spin" /> Vytváram...</> : "Platobný odkaz (karta)"}
              </button>
              <button onClick={() => create("bank")} disabled={busy} data-testid="preview-confirm-bank"
                className="flex-1 min-w-[180px] border border-[#D4AF37] text-[#D4AF37] font-semibold py-3 hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {busy ? <><Loader2 size={16} className="animate-spin" /> Odosielam...</> : <><Mail size={16} /> Odoslať PDF (bank. prevod)</>}
              </button>
              <button onClick={() => setPreview(false)} disabled={busy} data-testid="preview-back"
                className="border border-white/15 px-5 py-3 text-sm text-white/60 hover:text-white disabled:opacity-50">Upraviť</button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="relative">
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">Odberateľ (meno/firma) * <span className="text-[#D4AF37]/60 normal-case tracking-normal">— hľadá v našich záznamoch</span></label>
                <input value={b.buyer_name} data-testid="ni-name" autoComplete="off"
                  onChange={(e) => { set("buyer_name")(e); searchOurs(e.target.value); }}
                  onFocus={() => sug.length && setSugOpen(true)}
                  onBlur={() => setTimeout(() => setSugOpen(false), 160)}
                  className="w-full bg-transparent border-b border-white/20 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                {sugOpen && sug.length > 0 && (
                  <div className="absolute z-30 left-0 right-0 mt-1 bg-[#111] border border-white/15 rounded max-h-56 overflow-auto shadow-xl" data-testid="ni-name-suggestions">
                    {sug.map((s, i) => (
                      <button key={i} type="button" onClick={() => pickSug(s)} data-testid={`ni-sug-${i}`}
                        className="block w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-0">
                        <span className="text-white text-sm">{s.name}</span>
                        <span className="block text-white/40 text-xs">{[s.email, s.ico && ("IČO " + s.ico), s.vat_id && ("IČ DPH " + s.vat_id)].filter(Boolean).join(" · ")}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Inp label="E-mail *" type="email" value={b.buyer_email} onChange={set("buyer_email")} testid="ni-email" />
              <Inp label="Adresa" value={b.buyer_address} onChange={set("buyer_address")} testid="ni-address" />
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">IČO / Dát. narodenia</label>
                <div className="flex items-center gap-2">
                  <input value={b.buyer_ico} onChange={set("buyer_ico")} data-testid="ni-ico" autoCapitalize="none" autoCorrect="off"
                    placeholder="napr. 35757442"
                    className="flex-1 bg-transparent border-b border-white/20 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                  <button type="button" onClick={lookupCompany} disabled={lookupBusy} data-testid="ni-ico-lookup"
                    className="inline-flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#D4AF37] px-2.5 py-1.5 text-xs hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50 shrink-0">
                    {lookupBusy ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Vyhľadať
                  </button>
                </div>
                {lookupMsg && <p className="text-[11px] mt-1.5 text-white/50" data-testid="ni-lookup-msg">{lookupMsg}</p>}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">Krajina</label>
                <select value={b.buyer_country} onChange={set("buyer_country")} data-testid="ni-country"
                  className="w-full bg-[#0d0d0d] border-b border-white/20 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]">
                  {COUNTRIES.map(([c, n]) => <option key={c} value={c} className="bg-[#0d0d0d]">{n}</option>)}
                </select>
              </div>
              <Inp label="IČ DPH odberateľa (EÚ)" value={b.buyer_vat_id} onChange={set("buyer_vat_id")} testid="ni-vatid" />
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">Rad faktúry (číselná predpona)</label>
                <select value={b.series} onChange={(e) => setB((p) => ({ ...p, series: parseInt(e.target.value, 10) }))} data-testid="ni-series"
                  className="w-full bg-[#0d0d0d] border-b border-white/20 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]">
                  <option value={3} className="bg-[#0d0d0d]">3 — SauberEko s.r.o. (firma, ručne)</option>
                  <option value={2} className="bg-[#0d0d0d]">2 — Aplikácia SauberEko</option>
                </select>
              </div>
            </div>
            {b.buyer_country === "SK" && (
              <div className="border border-white/10 bg-[#111] p-4" data-testid="ni-apply-vat">
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">Uplatniť daň (DPH 23 %)</label>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setB((p) => ({ ...p, apply_vat: true }))} data-testid="ni-vat-yes"
                    className={`flex-1 py-2 text-sm font-semibold transition-colors ${b.apply_vat ? "bg-[#D4AF37] text-black" : "border border-white/15 text-white/60 hover:text-white"}`}>Áno — s DPH</button>
                  <button type="button" onClick={() => setB((p) => ({ ...p, apply_vat: false }))} data-testid="ni-vat-no"
                    className={`flex-1 py-2 text-sm font-semibold transition-colors ${!b.apply_vat ? "bg-[#D4AF37] text-black" : "border border-white/15 text-white/60 hover:text-white"}`}>Nie — bez DPH</button>
                </div>
                <p className="text-white/40 text-[11px] mt-2">Zvoľte „Nie", ak je služba poskytnutá v zahraničí a DPH sa neuplatňuje. Na faktúre sa uvedie poznámka o mieste dodania mimo SR.</p>
              </div>
            )}
            <p className="text-white/40 text-xs">Daňový režim: <span className="text-[#D4AF37]">{regimeHint}</span></p>

            <div className="space-y-3">
              <p className="text-xs uppercase tracking-wider text-white/40">Položky (sumy bez DPH)</p>
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-2" data-testid="ni-item-row">
                  <input placeholder="Služba / popis" value={it.description} onChange={setItem(i, "description")} data-testid="ni-item-desc"
                    className="flex-1 bg-transparent border-b border-white/20 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                  <input placeholder="0.00" type="number" step="0.01" value={it.amount} onChange={setItem(i, "amount")} data-testid="ni-item-amount"
                    className="w-28 bg-transparent border-b border-white/20 py-2 text-white text-sm text-right focus:outline-none focus:border-[#D4AF37]" />
                  <span className="text-white/40 text-sm">€</span>
                  {items.length > 1 && <button onClick={() => removeItem(i)} className="text-white/40 hover:text-red-400"><Trash2 size={16} /></button>}
                </div>
              ))}
              <button onClick={addItem} data-testid="ni-add-item" className="inline-flex items-center gap-1.5 text-[#D4AF37] text-sm hover:text-[#F2E3C6]"><Plus size={15} /> Pridať položku</button>
            </div>

            <div className="flex items-center justify-between border-t border-white/10 pt-4">
              <span className="text-white/60">Spolu</span>
              <span className="font-display text-2xl text-[#D4AF37]">{total.toFixed(2)} €</span>
            </div>

            {error && <p data-testid="ni-error" className="text-red-400 text-sm">{error}</p>}

            <button onClick={goPreview} disabled={busy} data-testid="ni-create"
              className="w-full bg-[#D4AF37] text-black font-semibold py-3.5 hover:bg-[#F2E3C6] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
              Skontrolovať náhľad
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Inp({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">{label}</label>
      <input {...props} data-testid={testid} autoCapitalize="none" autoCorrect="off"
        className="w-full bg-transparent border-b border-white/20 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
    </div>
  );
}
